import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle } from 'lucide-react';

// Helper para importar todas las imágenes de un directorio
// eslint-disable-next-line @typescript-eslint/no-var-requires
const importAll = (r: any): string[] => r.keys().map(r) as string[];

const clubesImages = importAll(
  // Imágenes de vista previa para "Clubes"
  // Carga todos los archivos .png/.jpg/.jpeg/.webp del directorio indicado
  (require as any).context('../assets/img/instituciones/uni', false, /\.(png|jpe?g|webp)$/)
);

const centrosImages = importAll(
  // Imágenes de vista previa para "Centros de entrenamiento"
  (require as any).context('../assets/img/instituciones/jo', false, /\.(png|jpe?g|webp)$/)
);

const beneficios = [
  {
    title: 'Identidad institucional real',
    text: (
      <>
        La app se adapta a los <strong>colores, escudo y estilo visual</strong> de tu institución para reforzar pertenencia y profesionalismo.
      </>
    ),
  },
  {
    title: 'Espacio exclusivo y privado',
    text: (
      <>
        Cada institución cuenta con un <strong>entorno propio</strong> para gestionar deportistas, entrenadores y planificación interna.
      </>
    ),
  },
  {
    title: 'Gestión centralizada del entrenamiento',
    text: (
      <>
        <strong>Organizá</strong> planes, seguí cargas, evaluaciones y evolución de atletas <strong>desde un solo lugar</strong>.
      </>
    ),
  },
  {
    title: 'Experiencia profesional para tus deportistas',
    text: (
      <>
        Brindales una <strong>herramienta moderna</strong> con la <strong>marca de tu club</strong>, elevando la percepción de <strong>valor y compromiso</strong>.
      </>
    ),
  },
  {
    title: 'Mayor orden y seguimiento del equipo técnico',
    text: (
      <>
        <strong>Facilitá</strong> el trabajo de entrenadores y coordinadores con una <strong>plataforma compartida y estructurada</strong>.
      </>
    ),
  },
  {
    title: 'Fortalece comunidad y pertenencia',
    text: (
      <>
        Una app diseñada para tu institución <strong>mejora el vínculo diario</strong> con atletas, familias y cuerpo técnico.
      </>
    ),
  },
];

const InstitucionesSection: React.FC = () => {
  const [clubIndex, setClubIndex] = useState(0);
  const [centroIndex, setCentroIndex] = useState(0);

  // Carrusel automático para Clubes
  useEffect(() => {
    if (clubesImages.length <= 1) return;
    const id = setInterval(() => {
      setClubIndex((i) => (i + 1) % clubesImages.length);
    }, 2500);
    return () => clearInterval(id);
  }, []);

  // Carrusel automático para Centros de entrenamiento
  useEffect(() => {
    if (centrosImages.length <= 1) return;
    const id = setInterval(() => {
      setCentroIndex((i) => (i + 1) % centrosImages.length);
    }, 2500);
    return () => clearInterval(id);
  }, []);

  return (
    <section id="instituciones" className="section-padding bg-gray-50">
      <div className="container-custom">
        {/* Título y descripción */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-mas10-dark mb-4">
            Instituciones con identidad. Una app propia, con tecnología +Trainer
          </h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-4xl mx-auto">
            Convertí +Trainer en una app alineada con tu <strong>club, academia o institución</strong>: mismo motor tecnológico y
            experiencia personalizada con tus <strong>colores, escudo y gestión centralizada</strong> para entrenadores y atletas.
          </p>
        </motion.div>

        {/* Tres columnas */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-10">
          {/* Columna 1: Clubes */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="bg-white rounded-3xl shadow-lg p-6 md:p-8 flex flex-col"
          >
            <h3 className="text-lg md:text-xl font-display font-bold text-mas10-dark text-center tracking-[0.15em] uppercase mb-4">
              Clubes
            </h3>
            <div className="flex-1 flex flex-col items-center justify-center">
              <div className="w-full max-w-xs mx-auto aspect-[9/16] bg-gray-100 rounded-3xl shadow-inner flex items-center justify-center overflow-hidden">
                {clubesImages.length > 0 && (
                  <AnimatePresence mode="wait" initial={false}>
                    <motion.img
                      key={clubIndex}
                      src={clubesImages[clubIndex]}
                      alt={`Vista previa clubes ${clubIndex + 1}`}
                      loading="lazy"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="w-full h-full object-contain"
                    />
                  </AnimatePresence>
                )}
              </div>
              {clubesImages.length > 1 && (
                <div className="flex justify-center gap-1.5 mt-4">
                  {clubesImages.map((_, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setClubIndex(i)}
                      aria-label={`Ir a imagen de clubes ${i + 1}`}
                      className={`w-2 h-2 rounded-full transition-colors ${
                        i === clubIndex ? 'bg-mas10-primary scale-125' : 'bg-gray-300'
                      }`}
                    />
                  ))}
                </div>
              )}
            </div>
          </motion.div>

          {/* Columna 2: Centros de entrenamiento */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
            className="bg-white rounded-3xl shadow-lg p-6 md:p-8 flex flex-col"
          >
            <h3 className="text-lg md:text-xl font-display font-bold text-mas10-dark text-center tracking-[0.15em] uppercase mb-4">
              Centros
            </h3>
            <div className="flex-1 flex flex-col items-center justify-center">
              <div className="w-full max-w-xs mx-auto aspect-[9/16] bg-gray-100 rounded-3xl shadow-inner flex items-center justify-center overflow-hidden">
                {centrosImages.length > 0 && (
                  <AnimatePresence mode="wait" initial={false}>
                    <motion.img
                      key={centroIndex}
                      src={centrosImages[centroIndex]}
                      alt={`Vista previa centros de entrenamiento ${centroIndex + 1}`}
                      loading="lazy"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="w-full h-full object-contain"
                    />
                  </AnimatePresence>
                )}
              </div>
              {centrosImages.length > 1 && (
                <div className="flex justify-center gap-1.5 mt-4">
                  {centrosImages.map((_, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setCentroIndex(i)}
                      aria-label={`Ir a imagen de centros de entrenamiento ${i + 1}`}
                      className={`w-2 h-2 rounded-full transition-colors ${
                        i === centroIndex ? 'bg-mas10-primary scale-125' : 'bg-gray-300'
                      }`}
                    />
                  ))}
                </div>
              )}
            </div>
          </motion.div>

          {/* Columna 3: Beneficios */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="bg-white rounded-3xl shadow-lg p-6 md:p-8 flex flex-col"
          >
            <h3 className="text-lg md:text-xl font-display font-bold text-mas10-dark text-center tracking-[0.15em] uppercase mb-4">
              Beneficios
            </h3>
            <div className="space-y-4 text-left">
              {beneficios.map((item) => (
                <div key={item.title} className="flex items-start gap-3">
                  <div className="mt-1 flex-shrink-0">
                    <div className="w-6 h-6 rounded-full bg-mas10-accent flex items-center justify-center">
                      <CheckCircle className="w-4 h-4 text-mas10-dark" />
                    </div>
                  </div>
                  <div>
                    <p className="font-semibold text-mas10-dark text-sm md:text-base">
                      {item.title}
                    </p>
                    <p className="text-gray-600 text-sm md:text-base">
                      {item.text}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default InstitucionesSection;

